/**
 * The Synergy Studio component catalog, from the Feature Overview ("Client-Facing Status
 * Components" and "Internal Monitoring Checklist"): ten components or groups, Import and
 * Export being parents with CSV and CEDE children (TDD §1.2).
 *
 * Check names are machine names derived from the checklist's wording; each entry's comment
 * quotes the check it stands for. Verisk Technical Services own the final names (TDD §1.4),
 * and a runner's checks must match the configured set exactly (§3), so change them here and
 * in the runners together. Capability ids follow the TDD's `import.cede` style for group
 * children and must match the Statuspage adapter's catalog ids when the adapter is wired in.
 *
 * Group nesting follows the id prefix (import.*, export.*), see domain/health.ts groupOf().
 */
export interface CatalogEntry {
  id: string;
  display_name: string;
  category: 'Platform Capabilities' | 'User Workflows & Analytics';
  source: string;
  checks: string[];
  /** Asynchronous jobs take minutes and status reflects completion, not submission. */
  async: boolean;
}

export const CATALOG: CatalogEntry[] = [
  {
    id: 'login.sso',
    display_name: 'Login & SSO',
    category: 'Platform Capabilities',
    source: 'gha-synergy-platform',
    checks: [
      'okta_login', // Successful login via Okta federation
      'session_token_validation', // Session/token validation
      'create_api_credentials', // Create API credentials in Okta
    ],
    async: false,
  },
  {
    id: 'synergy.drive',
    display_name: 'Synergy Drive',
    category: 'Platform Capabilities',
    source: 'gha-synergy-platform',
    checks: [
      'presigned_upload', // Request a pre-signed upload URL and upload a file into the synthetic tenant
      'presigned_download', // Request a pre-signed download URL and download an existing file
    ],
    async: false,
  },
  {
    id: 'tenant.mgmt',
    display_name: 'Tenant Management',
    category: 'Platform Capabilities',
    source: 'gha-synergy-platform',
    checks: [
      'list_users', // List users
      'create_user', // Create a user
      'deactivate_user', // Deactivate a user
    ],
    async: false,
  },
  {
    id: 'activity',
    display_name: 'Activity Monitor',
    category: 'Platform Capabilities',
    source: 'gha-synergy-platform',
    checks: [
      'list_recent_activities', // Get a list of recent activities
      'download_activity_log', // Download the activity log
    ],
    async: false,
  },
  {
    id: 'data.query',
    display_name: 'Data Query Workspace',
    category: 'Platform Capabilities',
    source: 'gha-synergy-platform',
    checks: [
      'list_schemas', // Get a list of schemas
      'execute_sql_query', // Execute a SQL query against the exposure schema (Redshift SQL)
    ],
    async: false,
  },
  {
    id: 'vpn',
    display_name: 'VPN Connectivity',
    category: 'Platform Capabilities',
    source: 'gha-synergy-platform',
    checks: [
      'vpn_reachability', // Reachability check against an endpoint reachable only through the VPN tunnel
    ],
    async: false,
  },
  {
    id: 'import.csv',
    display_name: 'CSV Import',
    category: 'User Workflows & Analytics',
    source: 'gha-synergy-imports',
    checks: [
      'get_mapping_sets', // Get mapping sets
      'csv_import_job_completes', // Trigger CSV import job; status reflects job completion, not submission
    ],
    async: true,
  },
  {
    id: 'import.cede',
    display_name: 'CEDE Import',
    category: 'User Workflows & Analytics',
    source: 'gha-synergy-imports',
    checks: [
      'cede_import_job_completes', // Trigger CEDE import job; status reflects job completion, not submission
    ],
    async: true,
  },
  {
    id: 'loss.analysis',
    display_name: 'Loss Analysis',
    category: 'User Workflows & Analytics',
    source: 'gha-synergy-analysis',
    checks: [
      'get_event_sets', // Get event sets
      'loss_analysis_job_completes', // Trigger loss analysis job against an existing exposure view; completion, asynchronous
    ],
    async: true,
  },
  {
    id: 'export.csv',
    display_name: 'CSV Export',
    category: 'User Workflows & Analytics',
    source: 'gha-synergy-exports',
    checks: [
      'csv_export_completes', // Trigger export to CSV of an existing loss analysis
    ],
    async: false,
  },
  {
    id: 'export.cede',
    display_name: 'CEDE Export',
    category: 'User Workflows & Analytics',
    source: 'gha-synergy-exports',
    checks: [
      'cede_export_completes', // Trigger export to CEDE of an existing exposure view
    ],
    async: false,
  },
  {
    id: 'program',
    display_name: 'Program Creation & Analysis',
    category: 'User Workflows & Analytics',
    source: 'gha-synergy-analysis',
    checks: [
      'create_program', // Create program (synchronous)
      'program_analysis_job_completes', // Run program analysis; status reflects job completion (asynchronous)
    ],
    async: true,
  },
];

export const CATALOG_IDS = new Set(CATALOG.map((c) => c.id));

/** The order components are listed in: Platform Capabilities first, then the workflows, groups together. */
export const DISPLAY_ORDER = CATALOG.map((c) => c.id);

/** Checks whose name says they wait for a job: they run for minutes, and a timeout is the usual failure. */
export function isJobCheck(name: string): boolean {
  return name.endsWith('_job_completes');
}
